<!-- 主布局组件：包含左侧阶段列表和右侧内容区 -->
<template>
  <div class="main-layout">
    <!-- 左侧阶段导航 -->
    <div class="sidebar" :class="{ collapsed: sidebarCollapsed }">
      <!-- 侧边栏头部 -->
      <div class="sidebar-header">
        <div class="app-brand">
          <i class="el-icon-notebook-2"></i>
          <span class="brand-text" v-if="!sidebarCollapsed">记单词</span>
        </div>
        <el-button
          type="text"
          icon="el-icon-s-operation"
          @click="toggleSidebar"
          class="collapse-btn"
        />
      </div>

      <!-- 用户信息简要 -->
      <div class="user-summary" v-if="!sidebarCollapsed">
        <div class="user-info">
          <el-avatar :size="40" icon="el-icon-user-solid" />
          <div class="user-details">
            <div class="username">{{ userDisplayName }}</div>
            <div class="user-coins">
              <i class="el-icon-coin"></i>
              <span>{{ userCoins }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 学习阶段列表 -->
      <div class="stages-section">
        <div class="section-title" v-if="!sidebarCollapsed">
          <i class="el-icon-collection"></i>
          我的课程
        </div>

        <div class="stages-list">
          <!-- 第一级：课程体系 -->
          <div
            v-for="category in learningStages"
            :key="'category-' + category.id"
            class="stage-category"
          >
            <!-- 分类标题 -->
            <div 
              class="category-header"
              :class="{ collapsed: !category.expanded }"
              @click="toggleCategory(category)"
              v-if="!sidebarCollapsed"
            >
              <div class="category-icon">
                <i :class="category.expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
              </div>
              <div class="category-name">{{ category.name }}</div>
              <div class="category-badge">{{ category.courseCount }}</div>
            </div>

            <!-- 第二级：具体课程 -->
            <transition name="expand">
              <div v-if="category.expanded || sidebarCollapsed" class="courses-list">
                <div
                  v-for="course in category.courses"
                  :key="'course-' + course.id"
                  class="stage-item"
                  :class="{
                    active: currentStage?.id === course.id,
                    completed: course.progress?.isCompleted,
                    locked: !course.isUnlocked,
                  }"
                  @click="selectStage(course, category)"
                >
                  <div class="stage-icon">
                    <i :class="course.icon"></i>
                    <div class="stage-progress" v-if="course.progress > 0">
                      <div
                        class="progress-bar"
                        :style="{ width: course.progress + '%' }"
                      ></div>
                    </div>
                  </div>

                  <div class="stage-content" v-if="!sidebarCollapsed">
                    <div class="stage-name">{{ course.name }}</div>
                    <!-- <div class="stage-meta">
                      <span class="difficulty" :class="course.difficulty">
                        {{ getDifficultyText(course.difficulty) }}
                      </span>
                    </div> -->
                  </div>

                  <!-- 锁定状态 -->
                  <div class="lock-overlay" v-if="!course.isUnlocked">
                    <i class="el-icon-lock"></i>
                  </div>

                  <!-- 完成徽章 -->
                  <div class="completion-badge" v-if="course.progress?.isCompleted">
                    <i class="el-icon-check"></i>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- 底部统计信息 -->
      <div class="sidebar-footer" v-if="!sidebarCollapsed">
        <div class="daily-stats">
          <div class="stat-item">
            <span class="stat-label">今日学习</span>
            <div class="progress-container">
              <div class="progress-bar-container">
                <div class="progress-bar" :style="{ width: dailyStats.progress + '%' }"></div>
              </div>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-label">连续天数</span>
            <span class="stat-value">{{ dailyStats.streakDays }}天</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content" :class="{ expanded: sidebarCollapsed }">
      <!-- 顶部工具栏 -->
      <div class="content-header">
        <div class="breadcrumb">
          <span class="current-stage" v-if="currentStage">
            <i :class="currentStage.icon"></i>
            {{ currentStage.name }}
          </span>
        </div>

        <div class="header-actions" style="display: none;">
          <!-- 学习模式切换 -->
          <el-radio-group v-model="learningMode" size="small">
            <el-radio-button label="review">复习模式</el-radio-button>
            <el-radio-button label="learn">学习模式</el-radio-button>
          </el-radio-group>

          <!-- 设置按钮 -->
          <el-dropdown @command="handleSettingCommand">
            <el-button type="text" icon="el-icon-setting" />
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="difficulty">调整难度</el-dropdown-item>
              <el-dropdown-item command="schedule">学习计划</el-dropdown-item>
              <el-dropdown-item command="audio">音频设置</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="content-body">
        <slot :current-stage="currentStage" :learning-mode="learningMode" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { homeApi } from "@/api/home";
import CourseAPI from "@/api/course";
import backgroundMusicManager from '@/utils/backgroundMusicManager';

export default {
  name: "MainLayout",
  data() {
    return {
      sidebarCollapsed: false,
      learningMode: "review", // review | learn
      currentStage: null,
      dailyStats: {
        reviewedCount: 0,
        streakDays: 0,
        progress: 0,
      },
      userCoins: 0,
      learningStages: [],
    };
  },
  computed: {
    ...mapState("user", ["userInfo"]),
    ...mapGetters("user", ["userDisplayName"]),
    userId() {
      return this.userInfo?.id || 1; // 默认用户ID为1
    },
  },
  mounted() {
    this.initializeStages();
    this.loadStatsData();
    this.initializeBackgroundMusic();
  },
  beforeDestroy() {
    // 清理事件监听
    this.$root.$off('settings-updated', this.onSettingsUpdated);
  },
  methods: {
    /**
     * 初始化阶段数据
     */
    async initializeStages() {
      try {
        // 从课程API获取学习阶段数据
        const response = await CourseAPI.getCourseList();
        console.log("课程API响应:", response);
        
        if (response.success) {
          // 转换为二级结构：分类（体系） -> 课程
          this.learningStages = response.data.map((category, index) => {
            console.log("处理分类:", category.name, "课程数:", category.courseCount);
            
            // 处理该分类下的所有课程
            const courses = (category.courses || []).map(course => {
              console.log("  处理课程:", course.name, "进度:", course.progress);
              return {
                id: course.id,
                name: course.name,
                description: course.description,
                icon: this.getStageIcon(course.level),
                difficulty: this.getStageDifficulty(course.level),
                wordCount: course.progress?.totalWords || 0,
                masteredWords: course.progress?.masteredWords || 0,
                progress: course.progress?.currentProgress || 0,
                completed: course.progress?.isCompleted || false,
                locked: !course.isUnlocked,
                isUnlocked: course.isUnlocked,
                unlockCondition: course.unlockCondition,
                // 保存原始课程数据
                courseData: course
              };
            });
            
            return {
              id: category.id,
              name: category.name,
              description: category.description,
              courseCount: category.courseCount || courses.length,
              isUnlocked: category.isUnlocked,
              expanded: index === 0, // 只默认展开第一个分类
              courses: courses
            };
          });
          
          console.log("处理后的学习阶段（二级结构）:", this.learningStages);
        } else {
          throw new Error(response.message || '获取课程列表失败');
        }
      } catch (error) {
        console.error("获取课程列表失败：", error);
      }

      // 设置当前阶段：优先恢复用户上次选择的阶段，保证切换页面/刷新后侧边栏仍高亮正确
      if (this.learningStages.length > 0) {
        let foundStage = null;

        // 1. 优先从 localStorage 恢复用户上次选择的阶段（按 id 匹配最新课程数据）
        try {
          const savedStage = JSON.parse(localStorage.getItem("selectedStage") || "null");
          if (savedStage && savedStage.id) {
            for (const category of this.learningStages) {
              const matched = category.courses.find((c) => c.id === savedStage.id);
              if (matched) {
                foundStage = matched;
                break;
              }
            }
          }
        } catch (e) {
          console.warn("恢复选中阶段失败:", e);
        }

        // 2. 兜底：第一个未完成且未锁定的课程
        if (!foundStage) {
          for (const category of this.learningStages) {
            foundStage = category.courses.find(
              (course) => !course.completed && course.isUnlocked
            );
            if (foundStage) break;
          }
        }

        // 3. 最终兜底：第一个课程
        this.currentStage = foundStage || this.learningStages[0]?.courses[0];

        if (this.currentStage) {
          // 第二个参数 false 表示这是初始化恢复，不是用户主动切换
          this.$emit("stage-selected", this.currentStage, false);
        }
      }
    },

    /**
     * 加载统计数据（合并金币和今日统计）
     */
    async loadStatsData() {
      try {
        // 从API获取统计数据
        const response = await homeApi.getTodayStats(this.userId);
        console.log("统计数据API响应:", response);
        
        // 由于API拦截器的处理，response已经是data.data的内容
        const data = response;
        
        // 检查数据结构，优先使用stats字段
        const stats = data.stats || data;
        
        // 设置用户金币
        this.userCoins = stats.total_coins || 0;
        console.log("成功获取金币:", this.userCoins);
        
        // 设置今日统计
        this.dailyStats = {
          reviewedCount: stats.reviewed_count || 0,
          streakDays: stats.streak_days || 1,
          progress: stats.total_words > 0 ? Math.round((stats.total_words / (stats.study_goals || 20)) * 100) : 0,
        };
        console.log("成功获取今日统计:", this.dailyStats);
        
      } catch (error) {
        console.error("加载统计数据失败：", error);
        // 如果API调用失败，使用默认值
        this.userCoins = 0;
        this.dailyStats = {
          reviewedCount: 0,
          streakDays: 1,
          progress: 0,
        };
      }
    },

    /**
     * 切换分类展开/折叠
     */
    toggleCategory(category) {
      if (this.sidebarCollapsed) return;
      category.expanded = !category.expanded;
    },

    /**
     * 选择阶段
     */
    selectStage(stage) {
      if (!stage.isUnlocked) {
        this.$message.warning(stage.unlockCondition || '该课程尚未解锁');
        return;
      }

      this.currentStage = stage;
      // 第二个参数 true 表示用户主动切换（触发后端保存并重新分配今日单词）
      this.$emit("stage-selected", stage, true);

      // 保存选择的阶段到本地存储
      localStorage.setItem("selectedStage", JSON.stringify(stage));
    },

    /**
     * 切换侧边栏
     */
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      this.$emit("sidebar-toggled", this.sidebarCollapsed);
    },

    /**
     * 获取难度文本
     */
    getDifficultyText(difficulty) {
      const difficultyMap = {
        easy: "简单",
        medium: "中等",
        hard: "困难",
        expert: "专家",
      };
      return difficultyMap[difficulty] || "未知";
    },

    /**
     * 根据课程等级获取图标
     */
    getStageIcon(level) {
      const iconMap = {
        1: "el-icon-star-off",
        2: "el-icon-star-on",
        3: "el-icon-trophy",
        4: "el-icon-medal",
        5: "el-icon-crown",
      };
      return iconMap[level] || "el-icon-star-off";
    },

    /**
     * 根据课程等级获取难度
     */
    getStageDifficulty(level) {
      if (level <= 1) return "easy";
      if (level <= 2) return "medium";
      if (level <= 3) return "hard";
      return "expert";
    },

    /**
     * 处理设置命令
     */
    handleSettingCommand(command) {
      switch (command) {
        case "difficulty":
          this.$message.info("难度调整功能开发中...");
          break;
        case "schedule":
          this.$message.info("学习计划功能开发中...");
          break;
        case "audio":
          this.$message.info("音频设置功能开发中...");
          break;
      }
    },

    /**
     * 初始化背景音乐
     */
    async initializeBackgroundMusic() {
      try {
        // 背景音乐管理器会自动初始化
        console.log('🎵 MainLayout: 背景音乐管理器已初始化');
        
        // 监听用户设置变化
        this.$root.$on('settings-updated', this.onSettingsUpdated);
      } catch (error) {
        console.error('初始化背景音乐失败:', error);
      }
    },

    /**
     * 用户设置更新时的回调
     */
    onSettingsUpdated(settings) {
      const audioSetting = settings.find(s => s.setting_key === 'auto_play_audio');
      if (audioSetting) {
        const isEnabled = audioSetting.setting_value === '1';
        backgroundMusicManager.setEnabled(isEnabled);
        console.log('🎵 背景音乐设置已更新:', isEnabled ? '开启' : '关闭');
      } else {
        console.log('🎵 未找到音频设置');
      }
    },

    /**
     * 更新课程进度
     */
    updateStageProgress(stageId, masteredWords) {
      // 在二级结构中查找课程
      for (const category of this.learningStages) {
        const course = category.courses.find((c) => c.id === stageId);
        if (course) {
          course.masteredWords = masteredWords;
          course.progress = (masteredWords / course.wordCount) * 100;
          course.completed = course.progress >= 100;

          // 如果完成当前课程，解锁下一课程
          if (course.completed) {
            const currentIndex = category.courses.findIndex(c => c.id === stageId);
            if (currentIndex < category.courses.length - 1) {
              const nextCourse = category.courses[currentIndex + 1];
              if (nextCourse.locked) {
                nextCourse.locked = false;
                this.$message.success(
                  `恭喜完成${course.name}！已解锁${nextCourse.name}`
                );
              }
            }
          }
          break;
        }
      }
    },
  },
};
</script>

<style scoped>
.main-layout {
  display: flex;
  height: calc(100vh - 60px);
  background-color: #f5f7fa;
}

/* 侧边栏 */
.sidebar {
  width: 280px;
  height: calc(100vh - 60px);
  background: white;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

.sidebar.collapsed {
  width: 60px;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.app-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #409eff;
}

.brand-text {
  transition: opacity 0.3s ease;
}

.collapse-btn {
  color: #909399;
  padding: 4px;
}

.user-summary {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-details {
  flex: 1;
}

.username {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.user-coins {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #e6a23c;
  font-weight: 500;
}

.user-coins i {
  font-size: 14px;
}


/* 学习阶段 */
.stages-section {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  margin-bottom: 0;
  min-height: 0;
}

.section-title {
  padding: 0 16px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stages-list {
  padding: 0 8px;
  max-height: calc(100vh - 300px); /* 调整高度，为头部、底部和标题留出空间 */
  overflow-y: auto; /* 垂直滚动 */
  overflow-x: hidden; /* 隐藏水平滚动 */
}

/* 自定义滚动条样式 */
.stages-list::-webkit-scrollbar {
  width: 4px;
}

.stages-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.stages-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}


/* 分类样式 */
.stage-category {
  margin-bottom: 8px;
}

.category-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin: 4px 8px;
  background: linear-gradient(135deg, #f5f7fa, #e8edf3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid #909399;
}

.category-header:hover {
  background: linear-gradient(135deg, #e8edf3, #dde4ec);
  border-left-color: #409eff;
}

.category-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #606266;
  transition: transform 0.3s ease;
}

.category-header.collapsed .category-icon {
  transform: rotate(-90deg);
}

.category-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-left: 8px;
}

.category-badge {
  background: #409eff;
  color: white;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
  font-weight: 500;
}

/* 课程列表 */
.courses-list {
  padding-left: 12px;
}

/* 展开/折叠动画 */
.expand-enter-active, .expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter, .expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to, .expand-leave {
  max-height: 1000px;
  opacity: 1;
}
.stages-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 为滚动区域添加平滑滚动 */
.stages-list {
  scroll-behavior: smooth;
}

/* 确保最后一个项目有足够的底部间距 */
.stages-list .stage-item:last-child {
  margin-bottom: 8px;
}

.stage-item {
  display: flex;
  align-items: center;
  padding: 12px 8px;
  margin: 4px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 60px;
}

.stage-item:hover {
  background-color: #f5f7fa;
}

.stage-item.active {
  background-color: #ecf5ff;
  border-left: 3px solid #409eff;
}

.stage-item.completed {
  background-color: #f0f9ff;
}

.stage-item.locked {
  opacity: 0.5;
  cursor: not-allowed;
}

.stage-icon {
  position: relative;
  width: 35px;
  height: 35px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  background: linear-gradient(135deg, #409eff, #409eff);
  margin-right: 10px;
}

.stage-item.completed .stage-icon {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stage-item.locked .stage-icon {
  background: #c0c4cc;
}

.stage-progress {
  position: absolute;
  bottom: -8px;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 1px;
}

.progress-bar {
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 1px;
  transition: width 0.3s ease;
}

.stage-content {
  flex: 1;
  min-width: 0;
}

.stage-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.stage-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.difficulty {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  color: white;
}

.difficulty.easy {
  background: #67c23a;
}
.difficulty.medium {
  background: #e6a23c;
}
.difficulty.hard {
  background: #f56c6c;
}
.difficulty.expert {
  background: #909399;
}

.lock-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  color: #c0c4cc;
  font-size: 14px;
}

.completion-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  color: #67c23a;
  font-size: 14px;
}

/* 侧边栏底部 */
.sidebar-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafbfc;
  margin-top: auto;
  flex-shrink: 0;
}

.daily-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-item:first-child {
  max-width: 45%; /* 进一步限制今日学习部分的宽度 */
  padding: 0 8px; /* 添加内边距，确保内容不贴边 */
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
}

/* 进度条容器 */
.progress-container {
  width: 35%; /* 进一步缩小进度条宽度，确保完全在区域内 */
  margin: 0 auto; /* 居中显示 */
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background-color: #e4e7ed;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 2px; /* 减少底部边距 */
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #409eff, #67c23a);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.progress-text {
  color: #409eff;
  font-weight: 600;
}

.progress-percentage {
  color: #67c23a;
  font-weight: 600;
}

/* 主内容区 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.main-content.expanded {
  margin-left: 0;
}

.content-header {
  background: white;
  padding: 16px 24px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.breadcrumb {
  display: flex;
  align-items: center;
}

.current-stage {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.content-body {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(-100%);
  }

  .sidebar.collapsed {
    transform: translateX(0);
    width: 280px;
  }

  .main-content {
    margin-left: 0;
  }
}

/* 滚动条样式 */
.stages-section::-webkit-scrollbar {
  width: 4px;
}

.stages-section::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.stages-section::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.stages-section::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
